/**
 * InstallAppBanner — sticky bottom banner that nudges mobile-web users
 * toward installing the Android app (or signals iOS coming soon).
 *
 * Design philosophy
 *   - Show only on mobile-web. Desktop users don't need it; their
 *     "device" is the laptop they're browsing on.
 *   - Show only on public pages (landing / browse / SEO blog / about).
 *     NEVER on auth, chat, forms, dashboard, settings — those flows
 *     are mid-task and a banner would be annoying.
 *   - Show only to anonymous visitors. Logged-in users already have
 *     an active web session; suggesting they switch to the app mid-use
 *     is jarring.
 *   - 7-day dismissal cooldown via localStorage. If a user clicks ×,
 *     don't ask again for a week.
 *   - Two states by platform:
 *       Android  → green "Get the app" CTA → opens Play Store with
 *                  utm_source=web_sticky_banner so we can measure
 *                  conversion in Play's acquisition reports.
 *       iOS      → neutral "iOS coming soon" message — still earns
 *                  the impression but sets expectations.
 *   - Bilingual via existing react-i18next setup (no new dependencies).
 *
 * Why a banner instead of a hero-CTA change
 *   The hero already shows the Google Play badge. The bottleneck is
 *   that users who scroll past it forget the app exists. A persistent
 *   bottom strip keeps the install option visible without competing
 *   with the primary "Register Now" CTA.
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, X, Smartphone } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { playStoreUrl } from '@/utils/playStore';
import { safeLocalStorage } from '../utils/safeStorage';

const DISMISS_KEY = 'wefaqna_install_banner_dismissed_at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Logged-in users get the nudge exactly ONCE, ever — never a second time.
// The flag is written the moment it first renders (not on dismiss), so even a
// user who ignores it and navigates away never sees it again. Keyed by user id
// so a shared browser doesn't rob the next person of their one impression.
const SEEN_KEY_PREFIX = 'wefaqna_app_nudge_shown:';

function hasSeenNudge(userId) {
  try {
    return safeLocalStorage.getItem(`${SEEN_KEY_PREFIX}${userId}`) === '1';
  } catch {
    // Private mode → treat as "seen" so we fail closed and never nag.
    return true;
  }
}

function markNudgeSeen(userId) {
  try {
    safeLocalStorage.setItem(`${SEEN_KEY_PREFIX}${userId}`, '1');
  } catch {/* private mode → silent fail */}
}

// Don't show on these paths — mid-flow pages where a banner would distract.
const HIDDEN_PATH_PREFIXES = [
  '/login', '/register', '/forgot-password', '/reset-password',
  '/bride-form', '/groom-form', '/edit-profile', '/select-preference',
  '/messages', '/dashboard', '/settings', '/notifications',
  '/complaint', '/rating', '/my-tickets', '/ticket', '/profile/',
  '/admin', '/card/',
];

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  // Mobile detection — narrow enough to avoid false positives on
  // Chromebooks (touch + Android user-agent) which we treat as desktop.
  if (/Android/i.test(ua) && /Mobi/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  return 'desktop';
}

function shouldHideForPath(pathname) {
  return HIDDEN_PATH_PREFIXES.some((p) => pathname.startsWith(p));
}

function isDismissedRecently() {
  try {
    const raw = safeLocalStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const dismissedAt = parseInt(raw, 10);
    if (Number.isNaN(dismissedAt)) return false;
    return Date.now() - dismissedAt < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function markDismissed() {
  try {
    safeLocalStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {/* private mode → silent fail */}
}

const InstallAppBanner = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const [platform, setPlatform] = useState('desktop');
  const [visible, setVisible] = useState(false);
  // Logged-in users see a value-framed, once-only variant; anonymous visitors
  // keep the original 7-day-cooldown install banner.
  const [isMemberNudge, setIsMemberNudge] = useState(false);

  // Decide visibility on mount + every route change.
  useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);

    // Hide rules that apply to everyone:
    //   - desktop (we only want mobile-web users)
    //   - hidden path prefixes (mid-flow pages — never interrupt a task)
    if (p === 'desktop') return setVisible(false);
    if (shouldHideForPath(location.pathname)) return setVisible(false);

    // Logged-in members. ~84% of our users are web-only, so they never get
    // push notifications for new messages — the app is a genuine upgrade for
    // them. We say so honestly, and we say it exactly ONCE. iOS members get
    // nothing: there is no app for them to install yet, so a nudge would only
    // frustrate.
    if (user) {
      if (p !== 'android') return setVisible(false);
      if (hasSeenNudge(user.id)) return setVisible(false);
      markNudgeSeen(user.id); // burn the single impression on first render
      setIsMemberNudge(true);
      return setVisible(true);
    }

    // Anonymous visitors — original behaviour, unchanged.
    if (isDismissedRecently()) return setVisible(false);
    setIsMemberNudge(false);
    setVisible(true);
  }, [user, location.pathname]);

  if (!visible) return null;

  const handleDismiss = () => {
    markDismissed();
    setVisible(false);
  };

  // Android: green install CTA. iOS: neutral "coming soon" badge.
  // (Members are Android-only by the time we get here — see the effect above.)
  const isIos = platform === 'ios';
  const href = isIos ? null : playStoreUrl({
    source: 'website',
    // Separate medium for the member nudge so Play's acquisition report shows
    // which of the two surfaces actually drives installs.
    medium: isMemberNudge ? 'member_app_nudge' : 'sticky_install_banner',
    campaign: isMemberNudge ? 'member_web_to_app' : 'mobile_web_install_nudge',
  });

  const title = isMemberNudge
    ? t('installBanner.memberTitle')
    : (isIos ? t('installBanner.iosTitle') : t('installBanner.androidTitle'));
  const subtitle = isMemberNudge
    ? t('installBanner.memberSubtitle')
    : (isIos ? t('installBanner.iosSubtitle') : t('installBanner.androidSubtitle'));

  return (
    <div
      role="region"
      aria-label={t('installBanner.aria')}
      className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-wefaqna-navy text-white shadow-2xl border-t-2 border-wefaqna-green/60 animate-slide-up-banner"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-wefaqna-green/15 border border-wefaqna-green/40 flex items-center justify-center">
          {isIos
            ? <Smartphone className="h-5 w-5 text-wefaqna-forest" />
            : <Download className="h-5 w-5 text-wefaqna-forest" />
          }
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm leading-tight">{title}</p>
          <p className="text-xs text-white/70 leading-tight mt-0.5 truncate">{subtitle}</p>
        </div>

        {!isIos && (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 bg-wefaqna-forest text-white font-bold text-sm px-4 py-2 rounded-lg hover:bg-wefaqna-forest-hover transition-colors"
            onClick={() => {
              // Telemetry: PostHog will auto-capture the outbound click;
              // we add the data-testid so Sentry can correlate sessions
              // that converted via the banner.
            }}
            data-testid="install-banner-cta"
          >
            {t('installBanner.cta')}
          </a>
        )}

        <button
          onClick={handleDismiss}
          aria-label={t('installBanner.dismissAria')}
          className="flex-shrink-0 p-1.5 text-white/60 hover:text-white transition-colors"
          data-testid="install-banner-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default InstallAppBanner;
